// ─────────────────────────────────────────────────────────────────────────────
// parametrosFiltro.ts — catálogo de TIPOS de filtro + PADRÕES (valores iniciais)
// dos parâmetros de um modelo cadastrado. Compartilhado pelo editor admin
// (selects de tipo/padrão) e pelo ExploradorTab (input + pré-preenchimento +
// normalização pro bind). Adicionar um tipo/padrão = 1 entrada aqui.
// ─────────────────────────────────────────────────────────────────────────────

import type { TipoParametro } from '../../../api/estatisticasModelos';

/** Tipos disponíveis no editor (ordem = exibição). */
export const TIPOS_PARAMETRO: { value: TipoParametro; label: string }[] = [
  { value: 'texto',    label: 'Texto' },
  { value: 'numero',   label: 'Número (inteiro)' },
  { value: 'decimal',  label: 'Decimal' },
  { value: 'data',     label: 'Data (dia/mês/ano)' },
  { value: 'mesAno',   label: 'Mês/Ano' },
  { value: 'ano',      label: 'Ano' },
  { value: 'lista',    label: 'Lista (multi)' },
  { value: 'booleano', label: 'Sim/Não' },
];

/**
 * Opções de PADRÃO por tipo (token → rótulo). Tipos ausentes daqui (texto/numero/
 * decimal/lista) usam um campo de valor LITERAL livre. 'data' tem as granularidades
 * pedidas (início do mês, início do ano, …).
 */
export const PADROES_POR_TIPO: Partial<Record<TipoParametro, { value: string; label: string }[]>> = {
  data: [
    { value: '',          label: '(vazio)' },
    { value: 'hoje',      label: 'Hoje' },
    { value: 'ontem',     label: 'Ontem' },
    { value: 'inicioMes', label: 'Início do mês' },
    { value: 'fimMes',    label: 'Fim do mês' },
    { value: 'inicioAno', label: 'Início do ano' },
    { value: 'fimAno',    label: 'Fim do ano' },
    { value: 'd7',        label: '7 dias atrás' },
    { value: 'd30',       label: '30 dias atrás' },
    { value: 'd90',       label: '90 dias atrás' },
  ],
  mesAno: [
    { value: '',            label: '(vazio)' },
    { value: 'mesAtual',    label: 'Mês atual' },
    { value: 'mesAnterior', label: 'Mês anterior' },
  ],
  ano: [
    { value: '',            label: '(vazio)' },
    { value: 'anoAtual',    label: 'Ano atual' },
    { value: 'anoAnterior', label: 'Ano anterior' },
  ],
  booleano: [
    { value: '',  label: '(vazio)' },
    { value: 'S', label: 'Sim' },
    { value: 'N', label: 'Não' },
  ],
};

/** True quando o padrão é um SELECT de tokens (não um valor literal livre). */
export function padraoEhSelect(tipo: TipoParametro): boolean {
  return PADROES_POR_TIPO[tipo] !== undefined;
}

/** Tipo do <input> HTML por tipo de parâmetro. */
export function inputHtmlType(tipo: TipoParametro): 'text' | 'number' | 'date' | 'month' {
  switch (tipo) {
    case 'numero':
    case 'ano':
    case 'decimal': return 'number';
    case 'data': return 'date';
    case 'mesAno': return 'month';
    default: return 'text';
  }
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const isoDate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const isoMes = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

/**
 * Resolve o `padrao` (token OU literal) → valor inicial do input, no formato do
 * próprio input (data ISO, mês 'AAAA-MM', etc). Tokens desconhecidos caem como literal.
 */
export function resolverPadrao(tipo: TipoParametro, padrao?: string): string {
  const p = (padrao ?? '').trim();
  if (!p) return '';
  const hoje = new Date();
  if (tipo === 'data') {
    switch (p) {
      case 'hoje':      return isoDate(hoje);
      case 'ontem':     return isoDate(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 1));
      case 'inicioMes': return isoDate(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
      case 'fimMes':    return isoDate(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0));
      case 'inicioAno': return isoDate(new Date(hoje.getFullYear(), 0, 1));
      case 'fimAno':    return isoDate(new Date(hoje.getFullYear(), 11, 31));
      case 'd7':        return isoDate(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 7));
      case 'd30':       return isoDate(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 30));
      case 'd90':       return isoDate(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 90));
      default:          return p;
    }
  }
  if (tipo === 'mesAno') {
    if (p === 'mesAtual')    return isoMes(hoje);
    if (p === 'mesAnterior') return isoMes(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));
    return p;
  }
  if (tipo === 'ano') {
    if (p === 'anoAtual')    return String(hoje.getFullYear());
    if (p === 'anoAnterior') return String(hoje.getFullYear() - 1);
    return p;
  }
  return p; // texto/numero/decimal/lista/booleano → literal
}

/** Normaliza o valor do INPUT → formato enviado ao backend (bind). */
export function normalizarParaBind(tipo: TipoParametro, valorInput: string): string {
  if (tipo === 'mesAno') return valorInput.replace('-', ''); // 'AAAA-MM' → 'AAAAMM'
  return valorInput; // data já ISO; ano/numero/decimal/texto/booleano direto
}
